import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Stack
} from '@mui/material'

import Modal, { IconTitle, Title } from 'packages/ui/ModalAction'

import { VISIBILITY_VALUES } from './constants'
import { VisibilityInCenter } from './types'
import { useBulkSetVisibilityInCenter } from './use-site-actions'

type BulkVisibilityActionProps = {
  selectedCount: number | null
}

const isVisibilityInCenter = (visibility: string): visibility is VisibilityInCenter =>
  VISIBILITY_VALUES.includes(visibility as VisibilityInCenter)

const BulkVisibilityAction = ({ selectedCount }: BulkVisibilityActionProps) => {
  const { t } = useTranslation()
  const { setVisibility, loading } = useBulkSetVisibilityInCenter()
  const [pendingVisibility, setPendingVisibility] = useState<VisibilityInCenter | null>(null)
  const label = t('admin_sites.selection.update_visibility')

  const handleSelectChange = (event: SelectChangeEvent<VisibilityInCenter | ''>) => {
    const nextVisibility = event.target.value
    if (!isVisibilityInCenter(nextVisibility)) return
    setPendingVisibility(nextVisibility)
  }

  const handleCancel = () => {
    setPendingVisibility(null)
  }

  const handleConfirm = async () => {
    if (!pendingVisibility) return
    await setVisibility(pendingVisibility)
    setPendingVisibility(null)
  }

  return (
    <>
      <Select<VisibilityInCenter | ''>
        value=""
        displayEmpty
        onChange={handleSelectChange}
        disabled={loading}
        size="small"
        renderValue={() => label}
        inputProps={{ 'aria-label': label }}
        sx={{
          height: 28,
          minWidth: 160,
          borderRadius: '8px',
          fontSize: 12,
          bgcolor: 'white',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#EFEFEF' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D7DAEA' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2570EB' }
        }}
      >
        {VISIBILITY_VALUES.map(visibility => (
          <MenuItem key={visibility} value={visibility} sx={{ fontSize: 12 }}>
            {t(`admin_sites.visibility.${visibility}`)}
          </MenuItem>
        ))}
      </Select>
      {
        pendingVisibility !== null && (
          <Modal onClose={handleCancel}>
            <IconTitle variant={'primary'} icon={'pen'} />
            <Title>{label}</Title>
            <Stack>
              <Typography sx={{ fontSize: 14, color: 'content.secondary' }}>
                {t('admin_sites.selection.confirm_visibility', {
                  count: selectedCount ?? 0,
                  visibility: pendingVisibility ? t(`admin_sites.visibility.${pendingVisibility}`) : ''
                })}
              </Typography>
            </Stack>
            <Stack direction={'row'} gap={2}>
              <Button variant="outlined" size="small" onClick={handleCancel} disabled={loading}>
                {t('ui_elements.cancel')}
              </Button>
              <Button variant="contained" size="small" onClick={handleConfirm} disabled={loading}>
                {t('ui_elements.confirm')}
              </Button>
            </Stack>
          </Modal>
        )
      }
    </>
  )
}

export default BulkVisibilityAction
